<template>
  <div class="base-mobile-swiper-gallery" :class="{ 'is-single': isSingleSlide }">
    <swiper
      :modules="[Navigation, Pagination]"
      :slides-per-view="effectiveSlidesPerView"
      :space-between="isSingleSlide ? 0 : 8"
      :slides-offset-before="isSingleSlide ? 0 : 16"
      :slides-offset-after="isSingleSlide ? 0 : 16"
      :loop="!isSingleSlide"
      :grab-cursor="true"
      v-bind="$attrs"
      :pagination="{ clickable: true }"
      @swiper="onSwiper"
    >
      <template v-if="$slots.default">
        <swiper-slide
          v-for="(child, index) in $slots.default()[0]?.children ?? []"
          :key="index"
        >
          <div
            class="swiper-slide-wrapper"
            @click.stop="onSlideClick"
          >
            <component :is="child" />
          </div>
        </swiper-slide>
      </template>
      <p class="font--b6 text-white-monochrome swiper-active-index">
        {{ activeIndex + 1 }}/{{ numberOfSlides?.value || 0 }}
      </p>
    </swiper>
  </div>
</template>

<script setup lang="ts">
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css/pagination";
import "swiper/css";
import { PopUpServices } from "~/services/PopUp/callPopUp";

//look https://swiperjs.com/swiper-api#parameters for avaliable props

const route = useRoute();
const isUnitPage = computed(() => Boolean(route.params.id));
interface Props {
  slidesPerView?: number | "auto" | undefined;
  goToUnit: string | undefined | null;
  lightboxImages?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  slidesPerView: "auto",
});

const callPopUp = PopUpServices();

const slots = useSlots();
const isSingleSlide = computed(() => {
  const children = slots.default?.()?.[0]?.children;
  return Array.isArray(children) && children.length === 1;
});
const effectiveSlidesPerView = computed(() =>
  isSingleSlide.value ? 1 : props.slidesPerView
);

const activeIndex = ref(0);
const numberOfSlides = ref(null);
const localePath = useLocalePath();

const swiperInst = ref<typeof Swiper | null>(null);
const onSwiper = (swiper: any) => {
  swiperInst.value = swiper;
  swiper.on("slideChange", () => {
    activeIndex.value = swiperInst.value.realIndex;
  });
};

numberOfSlides.value = computed(() => {
  if (swiperInst.value) {
    return swiperInst.value.slides.length;
  }
});

const navigateToUnitPage = (id: string | null | undefined) => {
  if (!id || isUnitPage.value) return;
  const route = localePath(`/property/${id}`);
  window.open(window.location.origin + route, "_blank");
};

const onSlideClick = () => {
  const imgs = (props.lightboxImages ?? []).filter(Boolean);
  if (imgs.length) {
    callPopUp.callPopUp({
      componentName: "base-gallery",
      maximized: true,
      data: {
        imgs,
        pickedPhotoIndex: activeIndex.value,
      },
    });
    return;
  }
  navigateToUnitPage(props.goToUnit);
};

watch(effectiveSlidesPerView, (newVal) => {
  if (swiperInst.value) {
    swiperInst.value.params.slidesPerView = newVal;
    swiperInst.value.update();
  }
});
</script>

<style scoped lang="scss">
.base-mobile-swiper-gallery {
  position: relative;
  width: 100%;
  --swiper-pagination-bottom: 0px;
  --swiper-pagination-bullet-size: 6px;
  --swiper-pagination-bullet-inactive-color: var(--gray-monochrome-40);
  --swiper-pagination-bullet-inactive-opacity: 1;
  --swiper-pagination-bullet-vertical-gap: 0px;
  --swiper-pagination-bullet-horizontal-gap: 2px;
  --swiper-pagination-color: var(--black-monochrome-60);

  @media (max-width: 59rem) {
    width: calc(100% + 32px);
    max-width: none;
    margin-left: -16px;
    margin-right: -16px;
    flex-shrink: 0;
    align-self: stretch;
  }

  .swiper {
    width: 100%;
    max-width: 100%;
    height: 240px;
    margin: 0;

    :deep(.swiper-pagination-bullet-active) {
      width: 16px;
      border-radius: 6px;
    }

    &-active-index {
      position: absolute;
      bottom: 35px;
      right: 9px;
      z-index: 3;
      min-width: 32px;
      background-color: var(--black-monochrome-60);
      padding: 8px;
      border-radius: 8px;
    }
  }

  &.is-single {
    .swiper-slide {
      width: 100%;
    }

    .swiper-active-index {
      display: none;
    }

    :deep(.swiper-pagination) {
      display: none;
    }
  }

  .swiper-slide {
    width: calc(100% - 56px);
    height: 100%;
    justify-content: stretch;
    align-items: stretch;

    &-wrapper {
      width: 100%;
      height: 100%;
      border-radius: 12px;
      overflow: hidden;
      cursor: zoom-in;

      > :deep(div) {
        width: 100%;
        height: 100%;
      }
    }

    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 12px;
    }
  }
}
</style>
